'use client';

import Box from '@mui/material/Box';
import Popper, { type PopperProps } from '@mui/material/Popper';
import React, { useEffect, useState } from 'react';

import type { PopperUnstyledProps } from '@mui/base';
import type { BoxProps } from '@mui/material';
import type { ComponentType, FC, MouseEventHandler } from 'react';

export interface AbsoluteHoverPopperProps
  extends Omit<PopperProps, 'open' | 'component'>, Omit<BoxProps, 'color' | 'ref'>
{
  open?: PopperProps['open'];
  placement: PopperUnstyledProps['placement'];
  component: ComponentType<{ open?: boolean }>;
  anchorEl?: HTMLElement | null;
  onOpen?: MouseEventHandler;
  onClose?: MouseEventHandler;
}

/**
 * HoverPopper
 * Popper opening on mouseover
 * dropdown
 * no style
 * requires a render-component
 */

export const AbsoluteHoverPopper: FC<AbsoluteHoverPopperProps> = ({
  component: RenderComponent,
  placement = 'bottom',
  children = null,
  sx,
  anchorEl: initialAnchorEl = null,
  onOpen = void 0,
  onClose = void 0,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>( initialAnchorEl );

  const handleOpen: MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl( event.currentTarget );
    onOpen?.( event );
  };

  const handleClose: MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl( null );
    onClose?.( event );
  };

  const open = Boolean( anchorEl );

  const id = open ? 'simple-popper' : void 0;

  useEffect( () => {
    setAnchorEl( initialAnchorEl );
  }, [initialAnchorEl] );

  return (
    <Box sx={sx} onMouseLeave={handleClose} onMouseEnter={handleOpen}>
      <RenderComponent open={open} aria-describedby={id} />
      <Popper
        disablePortal={true}
        placement={placement}
        id={id}
        open={open}
        {...props}
      >
        {children}
      </Popper>
    </Box>
  );
};
